import React from 'react';
import { Typography, Input, Space, Breadcrumb, message } from 'antd';
import { LoadingModal } from '@doer/frontend-components/modal';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';

import { ReloadOutlined, SyncOutlined, DownloadOutlined } from '@ant-design/icons';
import { Spinner } from '@doer/frontend-components/spinner';
import { ActionButton } from '@doer/frontend-components/buttons';
import { dimensions } from '@doer/frontend-components/theme';
import { motion } from 'framer-motion';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { useLibrary } from '../../context/library';
import { useBanners } from '../../context/banners';
import { useModal } from '../../context/modal';
import { useBannerFilters } from '../../hooks/banners/library/use-banner-filters';
import { useDownloadFolder } from '../../hooks/media/use-download-folder';
import { BannerCard } from './banner-viewer-card';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { getFolderName } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const { Text } = Typography;

// El botón "Refrescar reels" sólo aparece dentro de la carpeta de reels. Los reels
// se guardan con folder `reel` (display "Reel"); aceptamos reel/reels en cualquier
// caja por las dudas de variantes de nombre.
const isReelsFolder = (folder: string | undefined): boolean => {
  const segment = (folder ?? '').split('/').pop()?.trim().toLowerCase() ?? '';
  return segment === 'reel' || segment === 'reels';
};

const generateBreadcrumbItems = (
  currentFolder: string | undefined,
  onNavigate: (path: string) => void
): BreadcrumbItemType[] => {
  const items: BreadcrumbItemType[] = [
    {
      title: 'Todos',
      onClick: () => onNavigate('/library'),
      className: 'breadcrumb-item'
    }
  ];

  if (!currentFolder) {
    return items;
  }

  const pathParts = currentFolder.split('/');

  // Para cada parte, crear un item del breadcrumb
  pathParts.forEach((_, index) => {
    // Construir la ruta completa hasta este punto
    // Ejemplo: index 0 -> "Espn", index 1 -> "Espn/Futbol"
    const pathUpToHere = pathParts.slice(0, index + 1).join('/');

    const formattedName = getFolderName(pathUpToHere);

    items.push({
      title: formattedName,
      onClick: () => onNavigate(`/library/${encodeURIComponent(pathUpToHere)}`),
      className: 'breadcrumb-item'
    });
  });

  return items;
};

const BannerGrid: React.FC = () => {
  const { fontSize, screens, spacing } = useResponsiveSizes();
  const { banners, currentFolder, isLoading, isLoadingMore, hasMore, loadMore, refresh } = useLibrary();
  const { refreshAllBanners, refreshReels } = useBanners();

  const handleRefresh = React.useCallback(() => {
    refresh(); // recarga la primera página de la carpeta actual del library
    refreshAllBanners(); // mantiene sincronizado el set del dashboard / modal
  }, [refresh, refreshAllBanners]);

  const showReelsButton = isReelsFolder(currentFolder);
  const [refreshingReels, setRefreshingReels] = React.useState(false);

  const { downloadFolderBatch } = useDownloadFolder();
  const [downloadState, setDownloadState] = React.useState<{ current: number; total: number } | null>(null);
  const cancelDownloadRef = React.useRef(false);

  const handleDownloadFolder = React.useCallback(async () => {
    if (!currentFolder) return;
    cancelDownloadRef.current = false;
    setDownloadState({ current: 1, total: 1 });

    try {
      let batchIndex = 0;
      let totalBatches = 1;
      do {
        setDownloadState({ current: batchIndex + 1, total: totalBatches });
        const response = await downloadFolderBatch(currentFolder, batchIndex);
        totalBatches = response.totalBatches;
        batchIndex += 1;
        if (cancelDownloadRef.current) break;
      } while (batchIndex < totalBatches);

      if (cancelDownloadRef.current) {
        message.info(`Descarga cancelada (${batchIndex} de ${totalBatches} partes bajadas)`);
      } else {
        message.success(totalBatches > 1 ? `Descarga completa (${totalBatches} partes)` : 'Descarga iniciada');
      }
    } catch (err) {
      console.error('Error descargando carpeta:', err);
      message.error('No se pudo generar la descarga de la carpeta');
    } finally {
      setDownloadState(null);
    }
  }, [currentFolder, downloadFolderBatch]);

  const handleCancelDownload = React.useCallback(() => {
    cancelDownloadRef.current = true;
  }, []);

  const handleRefreshReels = React.useCallback(async () => {
    setRefreshingReels(true);
    try {
      await refreshReels(); // encola el re-scrape del handle del cliente vía Apify
      message.success('Buscando los últimos reels… pueden tardar unos segundos en aparecer.');
      // El scrape es async (Apify → cola → DynamoDB); recargamos la carpeta tras una
      // pausa para que se vean los reels nuevos sin que el usuario tenga que hacerlo.
      window.setTimeout(() => refresh(), 8000);
    } catch (err) {
      console.error('Error refrescando reels:', err);
      message.error('No se pudieron traer los reels. Probá de nuevo en un momento.');
    } finally {
      setRefreshingReels(false);
    }
  }, [refreshReels, refresh]);
  const { searchTerm, setSearchTerm, totalItems, filteredBanners } = useBannerFilters(banners);
  const { selectedAssetId } = useModal();
  const navigate = useNavigate();

  //  Generar los items del breadcrumb
  const breadcrumbItems = React.useMemo(
    () => generateBreadcrumbItems(currentFolder, navigate),
    [currentFolder, navigate]
  );

  const isModalOpen = !!selectedAssetId;
  const stableBannersRef = React.useRef(filteredBanners);

  if (!isModalOpen) {
    stableBannersRef.current = filteredBanners;
  }

  // Banners visibles. Mientras el modal está abierto congelamos la lista para que el
  // grid de fondo no se reordene/recargue.
  const visibleBanners = isModalOpen ? stableBannersRef.current : filteredBanners;

  // Sentinel del scroll infinito: cargar la próxima página del servidor al acercarse al final.
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  // No search: infinite scroll pulls the next page as you reach the bottom.
  const canLoadMore = hasMore && !searchTerm.trim();

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !canLoadMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '400px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [canLoadMore, loadMore, isLoadingMore]);

  // TEMP fix: while searching, pull every remaining page so the client-side filter
  // sees all matches (normal browsing stays paginated). Real fix = server-side search.
  React.useEffect(() => {
    if (searchTerm.trim() && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [searchTerm, hasMore, isLoadingMore, loadMore]);

  // Configuración responsive de columnas
  const columnsCountBreakPoints = {
    350: 1, // móvil
    640: 2, // tablet pequeña
    1024: 3, // tablet/laptop pequeño
    1440: 4, // desktop
    1920: 5 // pantallas grandes
  };

  const gutterBreakPoints = {
    350: 12,
    640: 16,
    1024: 20,
    1440: 24
  };

  const searchHeight = dimensions.controlHeight.xs;

  return (
    <div>
      <style>{`
        .library-search .ant-input-affix-wrapper,
        .library-search .ant-input-search-button {
          height: ${searchHeight}px !important;
        }
      `}</style>
      {/* Controles superiores */}
      <Space align='center' style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space align='center'>
          <Input.Search
            className='library-search'
            placeholder='Buscar...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            styles={{ input: { fontSize: 12 } }}
            allowClear
          />

          <ActionButton variant='outline' size='XS' onClick={handleRefresh} disabled={isLoading} title='Actualizar'>
            {isLoading ? <Spinner size={14} /> : <ReloadOutlined />}
          </ActionButton>

          {currentFolder && (
            <ActionButton
              variant='outline'
              size='XS'
              onClick={handleDownloadFolder}
              disabled={downloadState !== null}
              title='Descargar carpeta'
            >
              {downloadState !== null ? <Spinner size={14} /> : <DownloadOutlined />}
            </ActionButton>
          )}

          {showReelsButton && (
            <ActionButton
              variant='outline'
              size='XS'
              onClick={handleRefreshReels}
              disabled={refreshingReels}
              title='Trae los últimos reels de Instagram que todavía no están en el library'
            >
              {refreshingReels ? <Spinner size={14} /> : <SyncOutlined />}
              Refrescar reels
            </ActionButton>
          )}
        </Space>

        <Text style={{ fontSize: fontSize.xlg }}>
          {totalItems}
          {hasMore ? '+' : ''} assets
        </Text>
      </Space>

      {/* Grid */}
      <>
        <div style={{ display: 'flex' }}>
          <Breadcrumb
            items={breadcrumbItems}
            style={{
              fontSize: fontSize.xlg,
              marginBottom: spacing.sm
            }}
            separator={<ChevronRight width={screens.xs ? 10 : 15} />}
          />
        </div>

        {isLoading ? (
          <div
            style={{
              textAlign: 'center',
              margin: screens.xs ? '100px 0' : '150px 0',
              minHeight: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Spinner size={40} />
          </div>
        ) : banners.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              margin: '50px',
              color: '#999',
              minHeight: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text style={{ fontSize: fontSize.xlg }}>No hay assets en esta carpeta</Text>
          </div>
        ) : (
          <motion.div
            key={currentFolder || 'all'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <ResponsiveMasonry columnsCountBreakPoints={columnsCountBreakPoints} gutterBreakPoints={gutterBreakPoints}>
              <Masonry>
                {visibleBanners.map((banner, index) => (
                  <motion.div
                    key={banner.bannerId}
                    style={{ width: '100%' }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.5,
                      // Cap del delay para que las páginas apendeadas (índices altos) animen rápido.
                      delay: Math.min(index, 12) * 0.03,
                      ease: [0.25, 0.1, 0.25, 1]
                    }}
                  >
                    <BannerCard creativity={banner} />
                  </motion.div>
                ))}
              </Masonry>
            </ResponsiveMasonry>

            {isLoadingMore && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: `${spacing.lg}px 0` }}>
                <Spinner size={24} />
              </div>
            )}

            {/* Sentinel del IntersectionObserver para el scroll infinito */}
            {canLoadMore && <div ref={sentinelRef} style={{ height: 1 }} />}
          </motion.div>
        )}
      </>

      <LoadingModal
        open={downloadState !== null}
        title={
          downloadState && downloadState.total > 1
            ? `Preparando parte ${downloadState.current} de ${downloadState.total}…`
            : 'Generando tu descarga…'
        }
        subtitle={
          downloadState && downloadState.total > 1
            ? 'Se descarga en partes. Tu navegador puede pedir permiso.'
            : 'Puede tardar unos minutos. No cierres esta ventana.'
        }
        cancelLabel={downloadState && downloadState.total > 1 ? 'Cancelar' : undefined}
        onCancel={handleCancelDownload}
      />
    </div>
  );
};

export default BannerGrid;
